<template>
  <section class="invitee-detail-page">
    <AppPageTitleBar
      class="is-hidden-sm-up"
      :title="route.meta.title"
      :back-to="backRoute"
      back-label="Natrag"
    >
      <template #actions>
        <v-btn
          v-if="canEditInvitees"
          class="button button--secondary button--small body-medium-bold text-uppercase invitee-detail-page__edit-button"
          :disabled="isEventArchived"
          :to="editInviteeRoute"
          variant="outlined"
        >
          Uredi uzvanika
        </v-btn>
      </template>
    </AppPageTitleBar>

    <section
      v-if="inviteeDetail"
      class="page-stack grid-stack grid-gap-page-section"
    >
      <article class="page-panel page-panel--mobile-unframed invitee-detail grid-stack grid-gap-20 grid-gap-sm-24">
        <div class="invitee-detail__toolbar grid grid-sm-up grid-column-gap-16 align-items-center">
          <router-link
            class="back-link col-12 col-sm-6"
            :to="backRoute"
            aria-label="Natrag"
          >
            <v-icon
              icon="mdi-arrow-left"
              size="20"
            />
            <span>Natrag</span>
          </router-link>

          <v-btn
            v-if="canEditInvitees"
            class="button button--secondary button--small body-medium-bold text-uppercase invitee-detail-page__edit-button col-12 col-sm-6 justify-self-end"
            :disabled="isEventArchived"
            :to="editInviteeRoute"
            variant="outlined"
          >
            Uredi uzvanika
          </v-btn>
        </div>

        <header class="page-panel invitee-detail__header">
          <div class="invitee-detail__identity">
            <h2 class="invitee-detail__name">{{ inviteeDetail.fullName }}</h2>

            <div class="invitee-detail__summary body-1-regular">
              <span
                v-for="summaryItem in inviteeSummaryItems"
                :key="summaryItem"
                class="invitee-detail__summary-item"
              >
                {{ summaryItem }}
              </span>
            </div>
          </div>

          <v-btn
            class="button button--secondary button--small button-text-regular invitee-detail__qr-button"
            variant="outlined"
            @click="openQrCodeDialog"
          >
            Prikaži QR kod
          </v-btn>
        </header>

        <section class="invitee-detail__section grid-stack grid-gap-12">
          <h3>
            Dokumenti ({{ inviteeDetail.documents.length }})
          </h3>

          <div class="invitee-detail-list">
            <div class="invitee-detail-list__header text-mid grid grid-sm-up grid-column-gap-16 align-items-center">
              <span class="col-12 col-sm-5">Naziv dokumenta</span>
              <span class="col-12 col-sm-4">Status</span>
              <span class="col-12 col-sm-3 text-right">Akcija</span>
            </div>

            <div
              v-for="document in inviteeDetail.documents"
              :key="document.id"
              class="invitee-detail-list__row body-1-regular grid grid-row-gap-10 grid-column-gap-16 align-items-center"
            >
              <div class="invitee-detail-list__cell col-8 col-sm-5 order-1 order-sm-1">
                {{ document.name }}
              </div>

              <div class="invitee-detail-list__cell col-8 col-sm-4 order-3 order-sm-2">
                <span
                  :class="[
                    'invitee-detail-list__status',
                    `invitee-detail-list__status--${document.status}`,
                  ]"
                >
                  <v-icon
                    v-if="document.status === 'signed'"
                    icon="mdi-check-circle-outline"
                    size="16"
                  />
                  {{ document.statusLabel }}
                </span>
              </div>

              <div class="invitee-detail-list__action col-4 col-sm-3 order-2 order-sm-3 justify-self-end">
                <v-btn
                  v-if="canSignDocuments && document.status === 'signature-required'"
                  class="button button--primary button--small button-text-regular invitee-detail-list__action-button"
                  :to="getDocumentSigningRoute(document)"
                  variant="flat"
                >
                  Potpiši
                </v-btn>
                <v-btn
                  v-else-if="canDownloadDocuments && document.signedPdf"
                  class="button button--secondary button--small button-text-regular invitee-detail-list__action-button"
                  :loading="downloadingDocumentId === document.id"
                  variant="outlined"
                  @click="downloadSignedDocumentAsync(document)"
                >
                  <v-icon
                    icon="mdi-download-outline"
                    size="18"
                  />
                  Preuzmi
                </v-btn>
                <v-btn
                  v-else-if="canDownloadDocuments && document.status === 'signed'"
                  class="button button--secondary button--small button-text-regular invitee-detail-list__action-button"
                  disabled
                  variant="outlined"
                >
                  PDF nije dostupan
                </v-btn>
              </div>
            </div>
          </div>
        </section>

        <section class="invitee-detail__section grid-stack grid-gap-12">
          <h3>
            Aktivnosti ({{ inviteeDetail.activities.length }})
          </h3>

          <div class="invitee-detail-list">
            <div class="invitee-detail-list__header text-mid grid grid-sm-up grid-column-gap-16 align-items-center">
              <span class="col-12 col-sm-5">Naziv aktivnosti</span>
              <span class="col-12 col-sm-4">Status</span>
              <span class="col-12 col-sm-3 text-right">Akcija</span>
            </div>

            <div
              v-for="activity in inviteeDetail.activities"
              :key="activity.activityTypeId ?? activity.id"
              class="invitee-detail-list__row body-1-regular grid grid-row-gap-10 grid-column-gap-16 align-items-center"
            >
              <div class="invitee-detail-list__cell col-8 col-sm-5 order-1 order-sm-1">
                {{ getActivityDisplayName(activity) }}
              </div>

              <div class="invitee-detail-list__cell col-8 col-sm-4 order-3 order-sm-2">
                <span
                  v-if="activity.statusLabel"
                  class="invitee-detail-list__status invitee-detail-list__status--completed"
                >
                  <v-icon
                    icon="mdi-check-circle-outline"
                    size="16"
                  />
                  {{ activity.statusLabel }}
                </span>
              </div>

              <div class="invitee-detail-list__action col-4 col-sm-3 order-2 order-sm-3 justify-self-end">
                <v-btn
                  v-if="canTrackActivities && activity.actionLabel"
                  class="button button--small button-text-regular invitee-detail-list__action-button"
                  :class="activity.actionButtonClass"
                  :disabled="isEventArchived"
                  :to="getActivityTrackingRoute(activity)"
                  variant="flat"
                >
                  {{ activity.actionLabel }}
                </v-btn>
              </div>
            </div>
          </div>
        </section>

        <section class="activity-log">
          <h5 class="activity-log__title">Popis aktivnosti</h5>

          <div
            v-if="activityLogEntries.length"
            class="activity-log__list"
          >
            <article
              v-for="activityLog in activityLogEntries"
              :key="activityLog.id"
              class="activity-log__item"
            >
              <span class="activity-log__name">{{ activityLog.activityName }}</span>

              <dl class="activity-log__details">
                <div
                  v-if="activityLog.vehiclePlate"
                  class="activity-log__detail"
                >
                  <dt class="activity-log__label">Registracijska oznaka:</dt>
                  <dd class="activity-log__value">{{ activityLog.vehiclePlate }}</dd>
                </div>
                <div
                  v-if="activityLog.timestamp"
                  class="activity-log__detail"
                >
                  <dt class="activity-log__label">Vrijeme:</dt>
                  <dd class="activity-log__value">{{ formatDateTime(activityLog.timestamp) }}</dd>
                </div>
                <div
                  v-if="activityLog.startTimestamp"
                  class="activity-log__detail"
                >
                  <dt class="activity-log__label">Vrijeme početka:</dt>
                  <dd class="activity-log__value">{{ formatDateTime(activityLog.startTimestamp) }}</dd>
                </div>
                <div
                  v-if="activityLog.activity.type === 'test-drive'"
                  class="activity-log__detail activity-log__detail--comment"
                >
                  <dt class="activity-log__label">Komentar:</dt>
                  <dd class="activity-log__value">{{ activityLog.commentStarted || '/' }}</dd>
                </div>
                <div
                  v-if="activityLog.endTimestamp"
                  class="activity-log__detail"
                >
                  <dt class="activity-log__label">Vrijeme završetka:</dt>
                  <dd class="activity-log__value">{{ formatDateTime(activityLog.endTimestamp) }}</dd>
                </div>
                <div
                  v-if="activityLog.activity.type === 'test-drive' && activityLog.endTimestamp"
                  class="activity-log__detail activity-log__detail--comment"
                >
                  <dt class="activity-log__label">Komentar:</dt>
                  <dd class="activity-log__value">{{ activityLog.commentFinished || '/' }}</dd>
                </div>
                <div
                  v-if="activityLog.activity.type !== 'test-drive'"
                  class="activity-log__detail activity-log__detail--comment"
                >
                  <dt class="activity-log__label">Komentar:</dt>
                  <dd class="activity-log__value">{{ (activityLog.commentFinished ?? activityLog.commentStarted) || '/' }}</dd>
                </div>
              </dl>
            </article>
          </div>

          <p
            v-else
            class="activity-log__empty"
          >
            Nema evidentiranih aktivnosti.
          </p>
        </section>
      </article>
    </section>

    <InviteeQrCodeDialog
      v-if="inviteeDetail"
      v-model="qrCodeDialogOpen"
      :event-name="inviteeDetail.event.name"
      :invitee-name="inviteeDetail.fullName"
      :qr-code-image-url="inviteeDetail.qrCode"
    />
  </section>
</template>

<script setup>
import { computed, onWatcherCleanup, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

import AppPageTitleBar from '../../components/AppPageTitleBar.vue'
import InviteeQrCodeDialog from '../../components/InviteeQrCodeDialog.vue'
import { hasPermission, permissionNames } from '../../auth/authorization'
import { datetime, formatDateTime } from '../../helpers'
import { apiClient } from '../../services/apiClient'
import { getActivityEventAsync } from '../../services/offlineService'
import { showErrorToast, showRequestErrorToast } from '../../store/appStore'

const route = useRoute()

const eventId = computed(() => route.params.eventId)
const inviteeId = computed(() => route.params.inviteeId)
const inviteeDetail = ref(null)
const qrCodeDialogOpen = ref(false)
const downloadingDocumentId = ref(null)
const backRoute = computed(() => {
  if (route.query.from !== 'invitees-list') {
    return {
      name: 'event-detail-invitees',
      params: { eventId: eventId.value },
    }
  }

  return {
    name: 'invitees-list',
    query: {
      ...(route.query.expandedInviteeId
        ? { expandedInviteeId: route.query.expandedInviteeId }
        : {}),
      ...(route.query.page ? { page: route.query.page } : {}),
      ...(route.query.search ? { search: route.query.search } : {}),
    },
  }
})
const editInviteeRoute = computed(() => ({
  name: 'event-invitee-edit',
  params: {
    eventId: eventId.value,
    inviteeId: inviteeId.value,
  },
}))
const isEventArchived = computed(() => inviteeDetail.value?.event.isArchived ?? false)
const canDownloadDocuments = computed(() => hasPermission(permissionNames.eventsDocumentsDownload))
const canEditInvitees = computed(() => hasPermission(permissionNames.eventsInviteesEdit))
const canSignDocuments = computed(() => hasPermission(permissionNames.eventsDocumentsSign))
const canTrackActivities = computed(() => hasPermission(permissionNames.eventsActivityStart))
const inviteeSummaryItems = computed(() => [
  inviteeDetail.value?.company,
  inviteeDetail.value?.residence,
  inviteeDetail.value?.email,
  inviteeDetail.value?.phone,
  inviteeDetail.value?.partnerId ? `Partner ID: ${inviteeDetail.value.partnerId}` : null,
].filter(Boolean))
const activityLogEntries = computed(() => [...(inviteeDetail.value?.activityLog ?? [])]
  .sort((firstLogEntry, secondLogEntry) => (
    (secondLogEntry.endTimestamp ?? secondLogEntry.timestamp ?? secondLogEntry.startTimestamp) -
    (firstLogEntry.endTimestamp ?? firstLogEntry.timestamp ?? firstLogEntry.startTimestamp)
  )))

watch([eventId, inviteeId], getEventInviteeDetailAsync, { immediate: true })

async function getEventInviteeDetailAsync () {
  let isCurrentRequest = true
  onWatcherCleanup(() => { isCurrentRequest = false })

  qrCodeDialogOpen.value = false
  inviteeDetail.value = null

  try {
    const event = await getActivityEventAsync(eventId.value)
    if (!isCurrentRequest) {
      return
    }

    const invitee = event.invitees.find(({ id }) => id === inviteeId.value)

    if (!invitee) {
      throw new Error('The invitee is not assigned to this event.')
    }

    inviteeDetail.value = mapEventInviteeDetail(event, invitee)
  } catch (error) {
    console.error('Failed to load the event invitee detail.', error)
    if (isCurrentRequest) {
      showRequestErrorToast(error, 'Detalje uzvanika nije bilo moguće učitati.')
    }
  }
}

function getDocumentSigningRoute (document) {
  return {
    name: 'event-invitee-document-sign',
    params: {
      eventId: eventId.value,
      inviteeId: inviteeId.value,
      documentId: document.id,
    },
    query: route.query,
  }
}

async function downloadSignedDocumentAsync (inviteeDocument) {
  downloadingDocumentId.value = inviteeDocument.id

  try {
    const signedDocument = await apiClient.getAsync(
      `/api/events/${eventId.value}/invitee-documents/${inviteeId.value}/${inviteeDocument.id}/download`,
      { responseType: 'blob' }
    )
    const downloadUrl = URL.createObjectURL(signedDocument)
    const downloadLink = window.document.createElement('a')

    downloadLink.href = downloadUrl
    downloadLink.download = `${inviteeDocument.slug ?? inviteeDocument.id}.pdf`
    downloadLink.click()
    URL.revokeObjectURL(downloadUrl)
  } catch (error) {
    console.error('Failed to download the signed invitee document.', error)
    showRequestErrorToast(error, 'Potpisani dokument nije bilo moguće preuzeti.')
  } finally {
    downloadingDocumentId.value = null
  }
}

function getActivityTrackingRoute (activity) {
  return {
    name: 'event-invitee-activity-track',
    params: {
      eventId: eventId.value,
      inviteeId: inviteeId.value,
      activityTypeId: activity.activityTypeId ?? activity.id,
    },
    query: route.query,
  }
}

function getActivityDisplayName (activity) {
  return activity.activity.customFieldValue
    ? `${activity.activity.name}: ${activity.activity.customFieldValue}`
    : activity.activity.name
}

function openQrCodeDialog () {
  if (!inviteeDetail.value.qrCode) {
    showErrorToast('QR kod uzvanika nije dostupan.')
    return
  }

  qrCodeDialogOpen.value = true
}

// Adds the status fields used to display invitee documents.
function mapInviteeDocuments (documents) {
  return documents.map((document) => {
    const isSigned = Boolean(document.signedAt)

    return {
      ...document,
      status: isSigned ? 'signed' : 'signature-required',
      statusLabel: isSigned ? 'Potpisano' : 'Potpis potreban',
    }
  })
}

function createInviteeDocuments (eventDocuments, inviteeDocuments) {
  return eventDocuments.map((eventDoc) => {
    const inviteeDocument = inviteeDocuments.find((inviteeDoc) => (
      (inviteeDoc.documentTypeId ?? inviteeDoc.id) === eventDoc.id
    ))

    return {
      ...eventDoc,
      ...inviteeDocument
    }
  })
}

// Selects the activity record that currently needs to be shown and handled.
function getRelevantActivity (activities) {
  const activeActivity = activities.find(({ finishedAt, startedAt }) => (
    startedAt && !finishedAt
  ))

  if (activeActivity) {
    return activeActivity
  }

  const notStartedActivity = activities.find(({ startedAt }) => !startedAt)

  if (notStartedActivity) {
    return notStartedActivity
  }

  return [...activities].sort((firstActivity, secondActivity) => (
    datetime(secondActivity.startedAt) - datetime(firstActivity.startedAt)
  ))[0]
}

// Adds the status and action fields used to display an activity card.
function mapActivityCard (activity) {
  const isTestDrive = activity.activity.type === 'test-drive'
  const isStarted = Boolean(activity.startedAt)
  const hasActiveActivity = isStarted && !activity.finishedAt
  const isCompleted = !isTestDrive && isStarted

  return {
    ...activity,
    statusLabel: isCompleted ? 'Evidentirano' : '',
    actionLabel: isTestDrive
      ? (hasActiveActivity ? 'Završi aktivnost' : 'Pokreni aktivnost')
      : (isCompleted ? '' : 'Check-in'),
    actionButtonClass: isTestDrive
      ? hasActiveActivity
        ? 'invitee-detail-list__action-button--activity-finish'
        : 'invitee-detail-list__action-button--activity-start'
      : 'button--primary',
  }
}

// Creates one actionable card per activity type and places test drives last.
function createActivityCards (inviteeActivities) {
  const activityTypeIds = [
    ...new Set(inviteeActivities.map(({ activityTypeId, id }) => activityTypeId ?? id)),
  ]

  return activityTypeIds
    .map((activityTypeId) => {
      const matchingActivities = inviteeActivities.filter((activity) => (
        (activity.activityTypeId ?? activity.id) === activityTypeId
      ))

      return getRelevantActivity(matchingActivities)
    })
    .sort((firstActivity, secondActivity) => (
      Number(firstActivity.activity.type === 'test-drive') -
      Number(secondActivity.activity.type === 'test-drive')
    ))
    .map(mapActivityCard)
}

function createInviteeActivities (eventActivities, inviteeActivities, inviteeId) {
  return eventActivities.flatMap((activity) => {
    const activityRecords = inviteeActivities.filter(({ activityTypeId, id }) => (
      (activityTypeId ?? id) === activity.id
    ))

    return activityRecords.length
      ? activityRecords
      : [{
          id: activity.id,
          eventInviteeId: inviteeId,
          activityTypeId: activity.id,
          activity,
        }]
  })
}

// Creates log entries for completed activities and completed test drives.
function createActivityLog (inviteeActivities) {
  return inviteeActivities
    .filter(({ activity, finishedAt, startedAt }) => (
      Boolean(startedAt) && (activity.type !== 'test-drive' || Boolean(finishedAt))
    ))
    .map((activity) => {
      const isTestDrive = activity.activity.type === 'test-drive'

      return {
        ...activity,
        activityName: getActivityDisplayName(activity),
        timestamp: isTestDrive ? null : datetime(activity.startedAt),
        startTimestamp: isTestDrive ? datetime(activity.startedAt) : null,
        endTimestamp: activity.finishedAt ? datetime(activity.finishedAt) : null,
      }
    })
}

// Builds all data displayed on the event invitee detail page.
function mapEventInviteeDetail (event, invitee) {
  const inviteeActivities = createInviteeActivities(
    event.activities ?? [],
    invitee.activities ?? [],
    invitee.id
  )

  return {
    ...invitee,
    event,
    documents: mapInviteeDocuments(createInviteeDocuments(
      event.documents ?? [],
      invitee.documents ?? []
    )),
    activities: createActivityCards(inviteeActivities),
    activityLog: createActivityLog(inviteeActivities),
  }
}
</script>

<style
  scoped
  lang="scss"
>
.invitee-detail-page {
  overflow-x: hidden;

  &__edit-button.v-btn {
    width: 144px;
    height: clamp-bp("sm", "xl", 32, 40);
    padding: 0 clamp-bp("sm", "xl", 12, 16);
    background: var(--color-white);

    @include breakpoint-up("sm") {
      width: auto;
      min-width: 132px;
      background: transparent;
    }
  }
}

.invitee-detail {
  &__header {
    --page-panel-radius: 12px;

    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 0 16px;
    justify-content: space-between;
    box-shadow: var(--shadow-control);

    @include breakpoint-up("sm") {
      align-items: center;
      flex-wrap: nowrap;
      padding: 0;
      background: transparent;
      box-shadow: none;
    }
  }

  &__identity {
    display: contents;

    @include breakpoint-up("sm") {
      display: block;
      min-width: 0;
    }
  }

  &__name {
    flex: 1 1 0;
    min-width: 0;
  }

  &__summary {
    display: flex;
    flex-wrap: wrap;
    flex: 0 0 100%;
    gap: 4px 8px;
    margin-top: 8px;
    order: 3;
    color: var(--color-text-muted);
  }

  &__summary-item {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    overflow-wrap: anywhere;

    &:not(:last-child)::after {
      content: "•";
    }
  }

  &__qr-button.v-btn {
    flex: 0 0 auto;
    order: 2;
  }
}

.invitee-detail-list {
  overflow: hidden;
  border-radius: 8px;
  background: var(--color-white);
  box-shadow: var(--shadow-control);

  &__header {
    min-height: 42px;
    padding: 0 18px;
    border-bottom: 1px solid var(--color-border);
    color: var(--color-text-muted);
  }

  &__row {
    min-height: 72px;
    padding: 14px 16px;
    border-bottom: 1px solid var(--color-border);

    @include breakpoint-up("sm") {
      min-height: 64px;
      padding: 10px 18px;
    }

    &:last-child {
      border-bottom: 0;
    }
  }

  &__cell {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  &__action {
    grid-row: span 2;

    @include breakpoint-up("sm") {
      grid-row: auto;
    }
  }

  &__status {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: clamp-bp('sm', 'xl', 11, 12);
    font-weight: 400;
    line-height: 16px;

    &--signature-required {
      color: var(--color-status-signature-required);
    }

    &--signed,
    &--completed {
      color: var(--color-status-confirmed-text);
    }
  }

  &__action-button {
    &.v-btn {
      min-width: 84px;
    }

    &--activity-start.v-btn,
    &--activity-finish.v-btn {
      color: var(--color-white);
    }

    &--activity-start.v-btn {
      background: var(--color-activity-start-background);
    }

    &--activity-finish.v-btn {
      background: var(--color-activity-finish-background);
    }
  }
}

.activity-log {
  margin-top: 4px;

  &__title {
    margin: 0 0 8px;
  }

  &__list,
  &__empty {
    border-top: 1px solid var(--color-border);
  }

  &__item {
    padding: 12px 0;
    border-bottom: 1px solid var(--color-border);
  }

  &__name {
    display: block;
    font-size: 15px;
    font-weight: 300;
    line-height: 22px;
  }

  &__details {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 24px;
    margin: 3px 0 0;
    font-size: 13px;
    font-weight: 300;
    line-height: 20px;
  }

  &__detail {
    display: inline-flex;
    gap: 4px;

    &--comment {
      flex-basis: 100%;

      @include breakpoint-up("sm") {
        flex-basis: auto;
      }
    }
  }

  &__label {
    color: var(--color-text-muted);
  }

  &__value {
    margin: 0;
  }

  &__empty {
    margin: 0;
    padding: 16px 0;
    color: var(--color-text-muted);
    font-size: 12px;
    font-weight: 300;
    line-height: 18px;
  }
}
</style>
